/**
 * Hook: useConversations
 *
 * Gestisce conversazioni e messaggi WhatsApp.
 * Include polling automatico per aggiornamenti in tempo reale.
 *
 * @example
 * ```tsx
 * function ChatList() {
 *   const {
 *     conversations,
 *     selectedConversation,
 *     messages,
 *     selectConversation
 *   } = useConversations();
 *
 *   return (
 *     <div>
 *       {conversations.map(conv => (
 *         <div key={conv.id} onClick={() => selectConversation(conv)}>
 *           {conv.contact_name}
 *         </div>
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */

import { useState, useEffect } from 'react';
import { useWhatsAppConfig } from '../WhatsAppProvider';
import type { Conversation, Message } from '../types';
import { waAuthHeaders } from '../waFetch';

/**
 * Risultato dell'hook useConversations
 */
export interface UseConversationsResult {
  /** Lista conversazioni */
  conversations: Conversation[];
  /** Conversazione attualmente selezionata */
  selectedConversation: Conversation | null;
  /** Messaggi della conversazione selezionata */
  messages: Message[];
  /** Stato di caricamento */
  loading: boolean;
  /** Messaggio di errore se presente */
  error: string | null;
  /** Ricarica lista conversazioni */
  fetchConversations: () => Promise<void>;
  /** Seleziona una conversazione e carica i messaggi */
  selectConversation: (conversation: Conversation) => Promise<void>;
  /** Ricarica messaggi della conversazione corrente */
  refreshMessages: () => void;
  /** Elimina messaggi specifici per ID */
  deleteMessages: (messageIds: number[]) => Promise<any>;
  /** Elimina una conversazione con tutti i messaggi */
  deleteConversation: (conversationId: number) => Promise<any>;
  /** Pulisce tutti i log (richiede conferma) */
  clearLogs: (olderThanDays?: number) => Promise<any>;
}

/**
 * Hook per la gestione completa delle conversazioni WhatsApp.
 *
 * Fornisce:
 * - Lista conversazioni con auto-refresh
 * - Selezione conversazione e caricamento messaggi
 * - Polling automatico ogni N secondi (configurabile via Provider)
 * - Eliminazione messaggi e conversazioni
 *
 * @returns Oggetto con dati e metodi per gestire le conversazioni
 */
export function useConversations(): UseConversationsResult {
  const { apiBaseUrl, refreshInterval = 5000 } = useWhatsAppConfig();

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch tutte le conversazioni
   */
  const fetchConversations = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiBaseUrl}/conversations?limit=50`, { headers: waAuthHeaders() });
      if (!response.ok) throw new Error('Errore nel recupero conversazioni');

      const result = await response.json();
      setConversations(result.data || []);
    } catch (err: any) {
      setError(err.message);
      console.error('Errore fetch conversazioni:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch messaggi di una conversazione
   * @param conversationId - ID della conversazione
   */
  const fetchMessages = async (conversationId: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiBaseUrl}/conversations/${conversationId}/messages`, { headers: waAuthHeaders() });
      if (!response.ok) throw new Error('Errore nel recupero messaggi');

      const result = await response.json();
      setMessages(result.data || []);

      // Aggiorna la conversazione selezionata con i messaggi
      const conv = conversations.find(c => c.id === conversationId);
      if (conv) {
        setSelectedConversation({ ...conv, messages: result.data });
      }
    } catch (err: any) {
      setError(err.message);
      console.error('Errore fetch messaggi:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Segna conversazione come letta
   * @param conversationId - ID della conversazione
   */
  const markAsRead = async (conversationId: number) => {
    try {
      await fetch(`${apiBaseUrl}/conversations/${conversationId}/mark-read`, {
        method: 'POST',
        headers: waAuthHeaders(),
      });
      // Aggiorna unread_count localmente
      setConversations(prev =>
        prev.map(c => c.id === conversationId ? { ...c, unread_count: 0 } : c)
      );
    } catch (err) {
      console.error('Errore mark as read:', err);
    }
  };

  /**
   * Seleziona una conversazione e carica i messaggi
   * @param conversation - Conversazione da selezionare
   */
  const selectConversation = async (conversation: Conversation) => {
    setSelectedConversation(conversation);
    await fetchMessages(conversation.id);
    await markAsRead(conversation.id);
  };

  /**
   * Elimina messaggi specifici
   * @param messageIds - Array di ID messaggi da eliminare
   * @returns Risultato dell'operazione
   */
  const deleteMessages = async (messageIds: number[]) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiBaseUrl}/conversations/messages`, {
        method: 'DELETE',
        headers: waAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ messageIds }),
      });

      if (!response.ok) throw new Error('Errore durante l\'eliminazione dei messaggi');

      const result = await response.json();

      // Aggiorna lista messaggi rimuovendo quelli eliminati
      setMessages(prev => prev.filter(msg => !messageIds.includes(msg.id)));

      return result;
    } catch (err: any) {
      setError(err.message);
      console.error('Errore delete messages:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Elimina una conversazione con tutti i messaggi
   * @param conversationId - ID della conversazione da eliminare
   * @returns Risultato dell'operazione
   */
  const deleteConversation = async (conversationId: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiBaseUrl}/conversations/${conversationId}`, {
        method: 'DELETE',
        headers: waAuthHeaders(),
      });

      if (!response.ok) throw new Error('Errore durante l\'eliminazione della conversazione');

      const result = await response.json();

      // Rimuovi dalla lista locale
      setConversations(prev => prev.filter(c => c.id !== conversationId));

      // Se era selezionata, deseleziona
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(null);
        setMessages([]);
      }

      return result;
    } catch (err: any) {
      setError(err.message);
      console.error('Errore delete conversation:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Pulisci i log delle conversazioni
   * @param olderThanDays - Se specificato, elimina solo log più vecchi di N giorni
   * @returns Risultato dell'operazione
   */
  const clearLogs = async (olderThanDays?: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiBaseUrl}/conversations/clear-logs`, {
        method: 'DELETE',
        headers: waAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          confirm: 'DELETE_ALL_LOGS',
          olderThanDays,
        }),
      });

      if (!response.ok) throw new Error('Errore durante la cancellazione dei log');

      const result = await response.json();

      // Resetta lo stato
      setConversations([]);
      setMessages([]);
      setSelectedConversation(null);

      return result;
    } catch (err: any) {
      setError(err.message);
      console.error('Errore clear logs:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch iniziale e polling
  useEffect(() => {
    fetchConversations();

    // Poll per aggiornamenti
    const interval = setInterval(() => {
      fetchConversations();
      if (selectedConversation) {
        fetchMessages(selectedConversation.id);
      }
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [selectedConversation?.id, refreshInterval]);

  return {
    conversations,
    selectedConversation,
    messages,
    loading,
    error,
    fetchConversations,
    selectConversation,
    refreshMessages: selectedConversation
      ? () => fetchMessages(selectedConversation.id)
      : () => {},
    deleteMessages,
    deleteConversation,
    clearLogs
  };
}
